import { ClippingPolygonType, WallShapeType, WallType } from "../../types/floorplannerTypes";
import { createShapes } from "./createShapes";
import { FloorplannerStore } from '../../store/floorplannerStore';
import { checkLineIntersection } from "./checkLineIntersections";
import { editorStore } from "../../store/editorStore";
import { wallUpdateManager } from "../../utils/wallUpdateManager";
import { createFillMaterial } from "./PatternFillMaterial";
import { applyClippingPolygonsToShapes } from "./wallClippingGeometry";

// Extend Window interface for custom properties
declare global {
    interface Window {
        fillMeshWallsErrorTracking?: {
            totalErrors: number;
            errorsByType: Map<string, number>;
            errorsByWall: Map<string, number>;
            lastErrorTime: number;
            lastErrors: any[];
        };
    }
}

// Track which walls have already logged warnings to avoid console spam
// Use a WeakMap to persist across renders but allow garbage collection
const warnedWalls = new Set<string>();
const warnedClippingPolygons = new Set<string>();

// Clear warnings when a new floorplan is loaded (called from integrity check)
if (typeof window !== 'undefined') {
  (window as any).__clearFillMeshWarnings = () => {
    warnedWalls.clear();
    warnedClippingPolygons.clear();
  };
}

import intersect from '@turf/intersect';
import { polygon } from '@turf/helpers';
import * as THREE from 'three';
import {
    LineGeometry,
    LineMaterial,
    Line2,
    LineSegments2,
} from 'three-stdlib'
import {
    Feature,
    Polygon as GeoJSONPolygon,
    MultiPolygon as GeoJSONMultiPolygon,
    Geometry,
    Position,
    GeoJsonProperties,
} from 'geojson';
import kinks from '@turf/kinks';
import cleanCoords from '@turf/clean-coords';
import simplify from '@turf/simplify';
import buffer from '@turf/buffer';
import booleanIntersects from '@turf/boolean-intersects';
import { renderStore } from "../../store/renderStore";
import PerformanceMonitor from "../../utils/PerformanceMonitor";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { wallUsesDifferentSideColors } from "../../utils/fillSelection";
import {
  clipShapesToWallSide,
  resolveWallFaceAppearance,
} from "../../utils/wallFaceSides";
import { WallFaceSide } from "../../types/floorplannerTypes";

// Polygon validation helpers
const isClosed = (polygon: THREE.Vector2[]): boolean => {
    if (polygon.length < 3) return false;
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    return Math.abs(first.x - last.x) < 0.001 && Math.abs(first.y - last.y) < 0.001;
};

const hasMinVertices = (polygon: THREE.Vector2[], minVertices: number = 3): boolean => {
    return polygon.length >= minVertices;
};

const hasNonZeroArea = (polygon: THREE.Vector2[]): boolean => {
    if (polygon.length < 3) return false;

    // Calculate area using shoelace formula
    let area = 0;
    for (let i = 0; i < polygon.length; i++) {
        const j = (i + 1) % polygon.length;
        area += polygon[i].x * polygon[j].y;
        area -= polygon[j].x * polygon[i].y;
    }
    area = Math.abs(area) / 2;

    return area > 0.0001; // Small threshold to account for floating point precision
};

const hasValidCoordinates = (polygon: THREE.Vector2[]): boolean => {
    return polygon.every(point =>
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        !isNaN(point.x) &&
        !isNaN(point.y)
    );
};

const hasNoKinks = (polygonCoords: THREE.Vector2[]): boolean => {
    try {
        const coords = polygonCoords.map(p => [p.x, p.y]);
        const geoJsonPolygon = polygon([coords]);
        const kinkPoints = kinks(geoJsonPolygon);
        return kinkPoints.features.length === 0;
    } catch (error) {
        console.warn('Error checking for kinks:', error);
        return false;
    }
};

const validatePolygon = (polygon: THREE.Vector2[], operationType: string, wallId?: string): { isValid: boolean; reason?: string } => {
    if (!hasMinVertices(polygon, 3)) {
        return { isValid: false, reason: 'Insufficient vertices' };
    }

    if (!hasValidCoordinates(polygon)) {
        console.warn(`Invalid coordinates in ${operationType} polygon for wall ${wallId}:`, polygon);
        return { isValid: false, reason: 'Invalid coordinates (NaN or infinite)' };
    }

    if (!isClosed(polygon)) {
        return { isValid: false, reason: 'Polygon not closed' };
    }

    if (!hasNonZeroArea(polygon)) {
        return { isValid: false, reason: 'Zero area' };
    }

    if (!hasNoKinks(polygon)) {
        return { isValid: false, reason: 'Contains kinks' };
    }

    return { isValid: true };
};

export const fillMeshWall = (
    wall: WallType,
    filledLines: WallShapeType[],
    leftLines: WallShapeType[],
    rightLines: WallShapeType[],
    endCaps: WallShapeType[],
    newMeshWalls: THREE.Group,
    floorplannerStore: FloorplannerStore,
    scene: THREE.Scene,
) => {
    const {
        setWalls,
        wallWidth,
        fillColor,
        hoverColor,
    } = floorplannerStore;

    type Pair = [number, number];
    type Ring = Pair[];
    type Polygon = Ring[];
    type MultiPolygon = Polygon[];
    type Coord = number;
    type Point = [Coord, Coord];
    const noopRaycast = () => null;

    // Function to merge nearly coincident points (points closer than tolerance)
    const mergeClosePoints = (polygon: THREE.Vector2[], tolerance: number) => {
        return polygon.filter((p, i, arr) => {
            if (i === 0) return true;
            const prev = arr[i - 1];
            return p.distanceTo(prev) > tolerance;
        });
    };

    // Function to create meshes from shapes
    const createMeshFromShapes = (
        shapes: THREE.Shape[],
        material: THREE.Material,
        userDataType: string,
        parentGroup: THREE.Group,
    ) => {
        // Validate shapes before creating geometry to prevent NaN errors
        const validShapes = shapes.filter(shape => {
            const points = shape.getPoints();
            const hasValidPoints = points.every(point =>
                Number.isFinite(point.x) &&
                Number.isFinite(point.y) &&
                !isNaN(point.x) &&
                !isNaN(point.y)
            );

            if (!hasValidPoints) {
                console.warn(`Skipping shape with invalid points for ${userDataType}:`, points);
                return false;
            }

            return true;
        });

        if (validShapes.length === 0) {
            console.warn(`No valid shapes found for ${userDataType}, skipping mesh creation`);
            return;
        }

        const geometry = new THREE.ShapeGeometry(validShapes);
        const meshPart = new THREE.Mesh(geometry, material);
        meshPart.userData.type = userDataType;
        parentGroup.add(meshPart);
    };

    const createOutlines = (
        shapes: THREE.Shape[],
        clippingPolygons: ClippingPolygonType[] | undefined,
        color: number,
        dashed: boolean,
        dashSize: number,
        gapSize: number,
        parentGroup: THREE.Group,
        lineWidth: number = 1
    ) => {
        // Use simplified outline creation during progressive mode for better performance
        // Complex intersection calculations with clipping polygons are deferred
        const useSimplifiedOutlines = wallUpdateManager.isWallInProgressiveMode(wall.id);
        shapes.forEach((shape) => {
            const points = shape.getPoints();

            if (clippingPolygons && clippingPolygons.length > 0 && !useSimplifiedOutlines) {
                // Complex intersection calculations for accurate clipping during normal mode
                const start = points[0];
                const end = points[points.length - 1];
                const intersectionPoints: THREE.Vector2[] = [];

                // Collect intersection points with clipping polygons
                clippingPolygons.forEach((clippingPolygon) => {
                    const polygonPoints = clippingPolygon.polygon;
                    for (let i = 0; i < polygonPoints.length; i++) {
                        const p1 = polygonPoints[i];
                        const p2 = polygonPoints[(i + 1) % polygonPoints.length];
                        const intersection = checkLineIntersection(start, end, p1, p2);
                        if (intersection.intersecting && intersection.point) {
                            intersectionPoints.push(intersection.point);
                        }
                    }
                });

                intersectionPoints.push(start);
                intersectionPoints.push(end);
                intersectionPoints.sort((a, b) => a.distanceTo(start) - b.distanceTo(start));

                // Create separate line geometries for each segment
                for (let i = 0; i < intersectionPoints.length - 1; i += 2) {
                    const x1 = intersectionPoints[i].x;
                    const y1 = intersectionPoints[i].y;
                    const x2 = intersectionPoints[i + 1].x;
                    const y2 = intersectionPoints[i + 1].y;

                    // Separate geometry for each segment
                    const geometry = new LineGeometry();
                    geometry.setPositions([x1, y1, 0, x2, y2, 0]);

                    const material = new LineMaterial({
                        color: color,
                        linewidth: lineWidth,
                        dashed: dashed,
                        dashSize: dashSize,
                        gapSize: gapSize,
                        // CRITICAL: Set resolution for line thickness to work
                        resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
                    });

                    const lineSegment = new LineSegments2(geometry, material);
                    lineSegment.raycast = noopRaycast;

                    // Compute line distances for dashes if needed
                    if (dashed) {
                        lineSegment.computeLineDistances();
                    }

                    // Ensure the line segment is scaled correctly if needed
                    lineSegment.scale.set(1, 1, 1);

                    // Add to parent group
                    parentGroup.add(lineSegment);
                }
            } else {
                // Simplified outline creation: no clipping polygon intersections
                // Used both when no clipping polygons exist and during progressive mode
                for (let i = 0; i < points.length - 1; i++) {
                    const x1 = points[i].x;
                    const y1 = points[i].y;
                    const x2 = points[i + 1].x;
                    const y2 = points[i + 1].y;

                    // Separate geometry for each segment
                    const geometry = new LineGeometry();
                    geometry.setPositions([x1, y1, 0, x2, y2, 0]);

                    const material = new LineMaterial({
                        color: color,
                        dashSize: dashSize,
                        gapSize: gapSize,
                        linewidth: lineWidth,
                        dashed: dashed,
                    });

                    const lineSegment = new LineSegments2(geometry, material);
                    lineSegment.raycast = noopRaycast

                    // Compute line distances for dashes if needed
                    if (dashed) {
                        lineSegment.computeLineDistances();
                    }

                    // Ensure the line segment is scaled correctly
                    lineSegment.scale.set(1, 1, 1);

                    // Add to parent group
                    parentGroup.add(lineSegment);
                }
            }
        });
    };


    // Create shapes for the wall fill (inner shape only)
    // Note: leftLines, rightLines, and endCaps parameters are still passed to this function
    // but are not used here. They are processed by WallOutlineStructure for outline rendering.
    const customFillColor = (wall?.fillColor !== undefined && wall?.fillColor !== null) ? wall.fillColor : fillColor;
    const customFillOpacity = resolveFillOpacity(wall);
    const fillIsTransparent = customFillOpacity < 1;
    const resolvedWallWidth = wall.wallWidth || floorplannerStore.wallWidth;
    const useSplitSideFills =
      wall.lineForm !== "arc" && wallUsesDifferentSideColors(wall);

    newMeshWalls.userData.id = wall.id;

    const clippingPolygons = renderStore.wallClippingsMap.get(wall.id)

    // Create meshes for different parts of the wall
    try {
        const innerShapes = createShapes(
            filledLines[0],
            "inner",
            wall.lineWeight || floorplannerStore.wallLineWeight,
            resolvedWallWidth,
        );
        // Always apply clipping polygons so holes render consistently, even during progressive updates.
        if (clippingPolygons && clippingPolygons.length > 0) {
            applyClippingPolygonsToShapes(innerShapes, clippingPolygons, wall.id);
        }

        if (useSplitSideFills) {
            // Split the composed fill (miters / cutouts already applied) along the
            // centerline so Side 1 / Side 2 stay independent without rebuilding
            // from raw endpoints.
            const sides: WallFaceSide[] = ["left", "right"];
            sides.forEach((side) => {
                const sideShapes = clipShapesToWallSide(
                    innerShapes,
                    wall,
                    side,
                    resolvedWallWidth,
                );
                if (sideShapes.length === 0) return;
                const appearance = resolveWallFaceAppearance(wall, side);
                const sideColor =
                    appearance.fillColor !== undefined && appearance.fillColor !== null
                        ? appearance.fillColor
                        : customFillColor;
                const sideMaterial = createFillMaterial(sideColor, wall?.fillPattern, {
                    side: THREE.DoubleSide,
                    useWorldSpace: true,
                    opacity: customFillOpacity,
                    transparent: fillIsTransparent,
                    depthWrite: !fillIsTransparent,
                });
                createMeshFromShapes(
                    sideShapes,
                    sideMaterial,
                    `fill-${side}`,
                    newMeshWalls,
                );
            });
        } else {
            const filledMaterial = createFillMaterial(customFillColor, wall?.fillPattern, {
                side: THREE.DoubleSide,
                useWorldSpace: true, // walls use world-space pattern tiling
                opacity: customFillOpacity,
                transparent: fillIsTransparent,
                depthWrite: !fillIsTransparent,
            });
            createMeshFromShapes(innerShapes, filledMaterial, "fill", newMeshWalls);
        }
        // WallOutlineStructure always handles outline rendering (edge-based mode is always active)
        // Left/right outlines and endcaps are rendered by WallOutlineStructure component
        // No need to render them here to avoid double outline rendering
    } catch (error) {
        console.error('Error creating wall meshes:', error);
    }

}

// Global error tracking for debugging
if (typeof window !== 'undefined') {
    if (!window.fillMeshWallsErrorTracking) {
        window.fillMeshWallsErrorTracking = {
            totalErrors: 0,
            errorsByType: new Map(),
            errorsByWall: new Map(),
            lastErrorTime: 0,
            lastErrors: []
        };
    }
}
